import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { db } from "../firebase";
import { collection, doc, getDoc, setDoc, query, where, onSnapshot, writeBatch, deleteDoc, updateDoc, addDoc } from "firebase/firestore";
import Seite from "../components/Seite";
import {
  ITERATIONEN, neuesSalt, schluesselAbleiten, verschluesseln, entschluesseln, pruefwertErzeugen, pruefwertOk,
  passwortErzeugen, passwortStaerke, KATEGORIEN, arbeitsmappeImportieren,
} from "./passwoerterLogik";

const SAMMLUNG = "tresor";
const META = "tresor_meta";
const SPERRE_NACH_MS = 10 * 60 * 1000; // automatisch sperren nach 10 Minuten ohne Aktivität
const ZWISCHENABLAGE_LEEREN_MS = 30 * 1000;

const STAERKE = [
  { label: "leer", farbe: "#bbb" },
  { label: "schwach", farbe: "#e53935" },
  { label: "mittel", farbe: "#fb8c00" },
  { label: "stark", farbe: "#34a853" },
];
const katInfo = (key) => KATEGORIEN.find((k) => k.key === key) || KATEGORIEN[0];
const LEER = { titel: "", kategorie: "zugang", typ: "", organisation: "", benutzer: "", passwort: "", url: "", notizen: "", favorit: false, quelle: "manuell" };

async function inBatches(aktionen) {
  for (let i = 0; i < aktionen.length; i += 400) {
    const batch = writeBatch(db);
    aktionen.slice(i, i + 400).forEach((a) => a(batch));
    await batch.commit();
  }
}

// --- Einrichten / Entsperren ---------------------------------------------------------------
function Sperrbildschirm({ meta, onEntsperrt, onEinrichten }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [fehler, setFehler] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const neu = !meta;

  const absenden = async (ev) => {
    ev.preventDefault();
    setFehler("");
    if (neu) {
      if (pw.length < 12) { setFehler("Das Master-Passwort muss mindestens 12 Zeichen haben."); return; }
      if (pw !== pw2) { setFehler("Die beiden Eingaben stimmen nicht überein."); return; }
    }
    setLaeuft(true);
    try {
      if (neu) await onEinrichten(pw);
      else if (!(await onEntsperrt(pw))) setFehler("Master-Passwort falsch.");
    } finally { setLaeuft(false); setPw(""); setPw2(""); }
  };

  return (
    <form onSubmit={absenden} style={{ ...styles.karte, maxWidth: "460px" }}>
      <div style={styles.karteTitel}>{neu ? "🔐 Passwort-Tresor einrichten" : "🔒 Tresor gesperrt"}</div>
      {neu && (
        <p style={styles.hinweis}>
          Lege ein <b>Master-Passwort</b> fest. Damit werden alle Einträge in deinem Browser verschlüsselt, bevor sie gespeichert
          werden. <b>Ohne dieses Passwort sind die Daten nicht wiederherstellbar</b> – auch nicht von mir oder über Firebase.
          Am besten aufschreiben und sicher verwahren.
        </p>
      )}
      <input style={styles.input} type="password" autoFocus autoComplete={neu ? "new-password" : "current-password"} placeholder="Master-Passwort" value={pw} onChange={(e) => setPw(e.target.value)} />
      {neu && <>
        <input style={{ ...styles.input, marginTop: "0.5rem" }} type="password" autoComplete="new-password" placeholder="Master-Passwort wiederholen" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        <StaerkeBalken pw={pw} />
      </>}
      {fehler && <div style={styles.fehler}>{fehler}</div>}
      <button style={{ ...styles.button, marginTop: "0.7rem" }} disabled={laeuft} type="submit">{laeuft ? "Einen Moment …" : neu ? "Tresor anlegen" : "Entsperren"}</button>
    </form>
  );
}

function StaerkeBalken({ pw }) {
  const s = passwortStaerke(pw);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.3rem" }}>
      {[1, 2, 3].map((i) => <span key={i} style={{ flex: 1, height: "4px", borderRadius: "2px", backgroundColor: s >= i ? STAERKE[s].farbe : "#eee" }} />)}
      <span style={{ fontSize: "0.72rem", color: STAERKE[s].farbe, minWidth: "48px" }}>{STAERKE[s].label}</span>
    </div>
  );
}

// --- Bearbeiten ----------------------------------------------------------------------------
function EintragFormular({ eintrag, onSpeichern, onAbbrechen, onLoeschen }) {
  const [d, setD] = useState({ ...LEER, ...eintrag });
  const [zeigen, setZeigen] = useState(!eintrag?.passwort);
  const set = (k) => (e) => setD({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div style={styles.overlay} onClick={onAbbrechen}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.karteTitel}>{eintrag?.id ? "Eintrag bearbeiten" : "Neuer Eintrag"}</div>
        <div style={styles.formGrid}>
          <label style={styles.label}>Titel *<input style={styles.input} value={d.titel} onChange={set("titel")} autoFocus /></label>
          <label style={styles.label}>Kategorie
            <select style={styles.input} value={d.kategorie} onChange={set("kategorie")}>
              {KATEGORIEN.map((k) => <option key={k.key} value={k.key}>{k.icon} {k.label}</option>)}
            </select>
          </label>
          <label style={styles.label}>Art / Typ<input style={styles.input} value={d.typ} onChange={set("typ")} placeholder="z.B. Zugang, Lizenz, KAS" /></label>
          <label style={styles.label}>Organisation / Kunde / Provider<input style={styles.input} value={d.organisation} onChange={set("organisation")} /></label>
          <label style={styles.label}>Benutzer<input style={styles.input} value={d.benutzer} onChange={set("benutzer")} autoComplete="off" /></label>
          <label style={styles.label}>Passwort
            <div style={{ display: "flex", gap: "0.3rem" }}>
              <input style={{ ...styles.input, flex: 1, fontFamily: "monospace" }} type={zeigen ? "text" : "password"} value={d.passwort} onChange={set("passwort")} autoComplete="new-password" />
              <button type="button" style={styles.kleinBtn} title={zeigen ? "Verbergen" : "Anzeigen"} onClick={() => setZeigen(!zeigen)}>{zeigen ? "🙈" : "👁️"}</button>
              <button type="button" style={styles.kleinBtn} title="Sicheres Passwort erzeugen" onClick={() => { setD({ ...d, passwort: passwortErzeugen(20) }); setZeigen(true); }}>🎲</button>
            </div>
            <StaerkeBalken pw={d.passwort} />
          </label>
          <label style={{ ...styles.label, gridColumn: "1 / -1" }}>URL<input style={styles.input} value={d.url} onChange={set("url")} placeholder="https://…" /></label>
          <label style={{ ...styles.label, gridColumn: "1 / -1" }}>Notizen<textarea style={{ ...styles.input, minHeight: "90px", fontFamily: "inherit" }} value={d.notizen} onChange={set("notizen")} /></label>
          <label style={{ ...styles.label, flexDirection: "row", alignItems: "center", gap: "0.4rem" }}><input type="checkbox" checked={!!d.favorit} onChange={set("favorit")} /> ⭐ Favorit</label>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
          {eintrag?.id && <button style={styles.buttonRot} onClick={() => onLoeschen(eintrag)}>Löschen</button>}
          <span style={{ flex: 1 }} />
          <button style={styles.buttonGrau} onClick={onAbbrechen}>Abbrechen</button>
          <button style={styles.button} onClick={() => d.titel.trim() ? onSpeichern(d) : alert("Bitte einen Titel eingeben.")}>Speichern</button>
        </div>
      </div>
    </div>
  );
}

// --- Hauptseite ----------------------------------------------------------------------------
export default function Passwoerter({ user }) {
  const [meta, setMeta] = useState(undefined); // undefined = lädt, null = noch kein Tresor
  const [schluessel, setSchluessel] = useState(null);
  const [roh, setRoh] = useState([]);
  const [eintraege, setEintraege] = useState([]);
  const [suche, setSuche] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [nurProbleme, setNurProbleme] = useState("");
  const [sortierung, setSortierung] = useState({ spalte: "titel", richtung: 1 });
  const [sichtbar, setSichtbar] = useState({});
  const [bearbeiten, setBearbeiten] = useState(null);
  const [meldung, setMeldung] = useState("");
  const [importLaeuft, setImportLaeuft] = useState(false);
  const letzteAktivitaet = useRef(Date.now());

  useEffect(() => {
    getDoc(doc(db, META, user.uid)).then((s) => setMeta(s.exists() ? s.data() : null)).catch((e) => setMeldung("Fehler: " + e.message));
  }, [user.uid]);

  const sperren = useCallback(() => { setSchluessel(null); setEintraege([]); setSichtbar({}); setBearbeiten(null); }, []);

  // Automatische Sperre bei Inaktivität und beim Verlassen der Seite
  useEffect(() => {
    if (!schluessel) return;
    const aktiv = () => (letzteAktivitaet.current = Date.now());
    const ereignisse = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    ereignisse.forEach((e) => window.addEventListener(e, aktiv, { passive: true }));
    const t = setInterval(() => { if (Date.now() - letzteAktivitaet.current > SPERRE_NACH_MS) sperren(); }, 15000);
    return () => { ereignisse.forEach((e) => window.removeEventListener(e, aktiv)); clearInterval(t); };
  }, [schluessel, sperren]);
  useEffect(() => sperren, [sperren]); // beim Verlassen der Seite den Schlüssel verwerfen

  useEffect(() => {
    if (!schluessel) return;
    const q = query(collection(db, SAMMLUNG), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => setRoh(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => setMeldung("Fehler beim Laden: " + e.message));
  }, [schluessel, user.uid]);

  useEffect(() => {
    if (!schluessel) return;
    let abbruch = false;
    Promise.all(roh.map(async (r) => {
      try { return { ...LEER, ...(await entschluesseln(schluessel, r)), id: r.id, geaendert: r.geaendert }; }
      catch { return { ...LEER, id: r.id, titel: "⚠️ Nicht entschlüsselbar", kaputt: true }; }
    })).then((liste) => { if (!abbruch) setEintraege(liste); });
    return () => { abbruch = true; };
  }, [roh, schluessel]);

  const einrichten = async (pw) => {
    const salt = neuesSalt();
    const k = await schluesselAbleiten(pw, salt, ITERATIONEN);
    const neu = { uid: user.uid, salt, iterationen: ITERATIONEN, pruefwert: await pruefwertErzeugen(k), erstellt: new Date() };
    await setDoc(doc(db, META, user.uid), neu);
    setMeta(neu);
    letzteAktivitaet.current = Date.now();
    setSchluessel(k);
  };

  const entsperren = async (pw) => {
    const k = await schluesselAbleiten(pw, meta.salt, meta.iterationen);
    if (!(await pruefwertOk(k, meta.pruefwert))) return false;
    letzteAktivitaet.current = Date.now();
    setSchluessel(k);
    return true;
  };

  const speichern = async (d) => {
    const { id, geaendert, kaputt, ...daten } = d;
    const chiffrat = await verschluesseln(schluessel, daten);
    if (id) await updateDoc(doc(db, SAMMLUNG, id), { ...chiffrat, geaendert: new Date() });
    else await addDoc(collection(db, SAMMLUNG), { ...chiffrat, uid: user.uid, erstellt: new Date(), geaendert: new Date() });
    setBearbeiten(null);
  };

  const loeschen = async (e) => {
    if (!window.confirm(`„${e.titel}“ wirklich löschen?`)) return;
    await deleteDoc(doc(db, SAMMLUNG, e.id));
    setBearbeiten(null);
  };

  const excelImport = async (ev) => {
    const datei = ev.target.files[0];
    ev.target.value = "";
    if (!datei) return;
    try {
      const wb = XLSX.read(await datei.arrayBuffer());
      const { eintraege: neu, info } = arbeitsmappeImportieren(XLSX, wb);
      if (!neu.length) { setMeldung("In der Datei wurden keine Einträge erkannt."); return; }
      const schonDa = new Set(eintraege.map((e) => `${e.titel}|${e.benutzer}|${e.passwort}`));
      const zuImportieren = neu.filter((e) => !schonDa.has(`${e.titel}|${e.benutzer}|${e.passwort}`));
      if (!window.confirm(`${info.join("\n")}\n\n${zuImportieren.length} neue Einträge verschlüsseln und übernehmen?${neu.length - zuImportieren.length ? `\n(${neu.length - zuImportieren.length} bereits vorhanden, werden übersprungen)` : ""}`)) return;
      setImportLaeuft(true);
      const chiffrate = await Promise.all(zuImportieren.map((e) => verschluesseln(schluessel, e)));
      const jetzt = new Date();
      await inBatches(chiffrate.map((c) => (b) => b.set(doc(collection(db, SAMMLUNG)), { ...c, uid: user.uid, erstellt: jetzt, geaendert: jetzt })));
      setMeldung(`${zuImportieren.length} Einträge verschlüsselt übernommen. Die Excel-Datei kann jetzt gelöscht werden.`);
    } catch (e) {
      setMeldung("Import fehlgeschlagen: " + e.message);
    } finally { setImportLaeuft(false); }
  };

  const excelExport = () => {
    if (!window.confirm("Der Export enthält alle Passwörter UNVERSCHLÜSSELT. Datei nur sicher ablegen und nach Gebrauch löschen. Fortfahren?")) return;
    const zeilen = eintraege.filter((e) => !e.kaputt).map((e) => ({
      Titel: e.titel, Kategorie: katInfo(e.kategorie).label, "Art/Typ": e.typ, Organisation: e.organisation,
      Benutzer: e.benutzer, Passwort: e.passwort, URL: e.url, Notizen: e.notizen, Favorit: e.favorit ? "ja" : "", Quelle: e.quelle,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(zeilen), "Passwörter");
    XLSX.writeFile(wb, `Passwoerter_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const masterAendern = async () => {
    const alt = window.prompt("Aktuelles Master-Passwort:");
    if (!alt) return;
    const kAlt = await schluesselAbleiten(alt, meta.salt, meta.iterationen);
    if (!(await pruefwertOk(kAlt, meta.pruefwert))) { alert("Aktuelles Master-Passwort falsch."); return; }
    const neu = window.prompt("Neues Master-Passwort (mind. 12 Zeichen):");
    if (!neu || neu.length < 12) { alert("Abgebrochen – mindestens 12 Zeichen."); return; }
    if (window.prompt("Neues Master-Passwort wiederholen:") !== neu) { alert("Eingaben stimmen nicht überein."); return; }
    const salt = neuesSalt();
    const kNeu = await schluesselAbleiten(neu, salt, ITERATIONEN);
    const chiffrate = await Promise.all(eintraege.filter((e) => !e.kaputt).map(async ({ id, geaendert, kaputt, ...d }) => ({ id, c: await verschluesseln(kNeu, d) })));
    await inBatches(chiffrate.map(({ id, c }) => (b) => b.update(doc(db, SAMMLUNG, id), c)));
    const neuMeta = { ...meta, salt, iterationen: ITERATIONEN, pruefwert: await pruefwertErzeugen(kNeu), geaendert: new Date() };
    await setDoc(doc(db, META, user.uid), neuMeta);
    setMeta(neuMeta);
    setSchluessel(kNeu);
    setMeldung("Master-Passwort geändert, alle Einträge neu verschlüsselt.");
  };

  const kopieren = async (text, was) => {
    try {
      await navigator.clipboard.writeText(text);
      setMeldung(`${was} kopiert${was === "Passwort" ? " – wird in 30 Sekunden aus der Zwischenablage entfernt" : ""}.`);
      if (was === "Passwort") {
        setTimeout(async () => {
          try { if ((await navigator.clipboard.readText()) === text) await navigator.clipboard.writeText(""); } catch { /* Lesen nicht erlaubt */ }
        }, ZWISCHENABLAGE_LEEREN_MS);
      }
    } catch { setMeldung("Kopieren nicht möglich."); }
  };

  // Auswertungen: mehrfach verwendete und schwache Passwörter
  const mehrfach = useMemo(() => {
    const z = {};
    eintraege.forEach((e) => { if (e.passwort) z[e.passwort] = (z[e.passwort] || 0) + 1; });
    return z;
  }, [eintraege]);
  const problem = (e) => ({ mehrfach: e.passwort && mehrfach[e.passwort] > 1, schwach: passwortStaerke(e.passwort) === 1, leer: !e.passwort });

  const SPALTEN = [
    { key: "titel", label: "Titel", wert: (e) => e.titel.toLowerCase() },
    { key: "kategorie", label: "Kategorie", wert: (e) => katInfo(e.kategorie).label },
    { key: "organisation", label: "Organisation", wert: (e) => (e.organisation || "").toLowerCase() },
    { key: "benutzer", label: "Benutzer", wert: (e) => (e.benutzer || "").toLowerCase() },
    { key: "passwort", label: "Passwort", wert: (e) => passwortStaerke(e.passwort) },
    { key: "url", label: "URL", wert: (e) => e.url || "" },
  ];

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();
    const sp = SPALTEN.find((x) => x.key === sortierung.spalte) || SPALTEN[0];
    return eintraege
      .filter((e) => !kategorie || (kategorie === "favorit" ? e.favorit : e.kategorie === kategorie))
      .filter((e) => !nurProbleme || problem(e)[nurProbleme])
      .filter((e) => !s || [e.titel, e.organisation, e.benutzer, e.url, e.typ, e.notizen].join(" ").toLowerCase().includes(s))
      .sort((a, b) => (b.favorit ? 1 : 0) - (a.favorit ? 1 : 0) || (() => { const x = sp.wert(a), y = sp.wert(b); return (typeof x === "number" ? x - y : String(x).localeCompare(String(y), "de")) * sortierung.richtung; })());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eintraege, suche, kategorie, nurProbleme, sortierung, mehrfach]);

  if (meta === undefined) return <Seite titel="🔐 Passwörter"><p style={styles.leer}>Lädt …</p>{meldung && <div style={styles.fehler}>{meldung}</div>}</Seite>;
  if (!schluessel) return <Seite titel="🔐 Passwörter"><Sperrbildschirm meta={meta} onEntsperrt={entsperren} onEinrichten={einrichten} /></Seite>;

  const anzahl = (f) => eintraege.filter((e) => problem(e)[f]).length;

  return (
    <Seite titel="🔐 Passwörter">
      <div style={styles.kacheln}>
        <Kachel wert={eintraege.length} label="Einträge" farbe="#1a3558" />
        <Kachel wert={anzahl("mehrfach")} label="Passwort mehrfach verwendet" farbe="#e53935" aktiv={nurProbleme === "mehrfach"} onClick={() => setNurProbleme(nurProbleme === "mehrfach" ? "" : "mehrfach")} />
        <Kachel wert={anzahl("schwach")} label="Schwache Passwörter" farbe="#fb8c00" aktiv={nurProbleme === "schwach"} onClick={() => setNurProbleme(nurProbleme === "schwach" ? "" : "schwach")} />
        <Kachel wert={anzahl("leer")} label="Ohne Passwort" farbe="#888" aktiv={nurProbleme === "leer"} onClick={() => setNurProbleme(nurProbleme === "leer" ? "" : "leer")} />
      </div>

      <div style={styles.leiste}>
        <input style={{ ...styles.input, flex: 2, minWidth: "200px" }} placeholder="🔍 Suche in Titel, Organisation, Benutzer, URL, Notizen …" value={suche} onChange={(e) => setSuche(e.target.value)} />
        <button style={styles.button} onClick={() => setBearbeiten({})}>+ Neuer Eintrag</button>
        <label style={{ ...styles.buttonGrau, display: "inline-block" }}>
          {importLaeuft ? "Importiere …" : "⬆ Excel-Import"}
          <input type="file" accept=".xlsx,.xls" style={{ display: "none" }} onChange={excelImport} disabled={importLaeuft} />
        </label>
        <button style={styles.buttonGrau} onClick={excelExport}>⬇ Export</button>
        <button style={styles.buttonGrau} onClick={masterAendern}>Master-Passwort ändern</button>
        <button style={styles.buttonDunkel} onClick={sperren}>🔒 Sperren</button>
      </div>

      <div style={styles.leiste}>
        {[{ key: "", label: "Alle" }, { key: "favorit", label: "⭐ Favoriten" }, ...KATEGORIEN.map((k) => ({ key: k.key, label: `${k.icon} ${k.label}` }))].map((k) => (
          <button key={k.key} style={{ ...styles.chip, backgroundColor: kategorie === k.key ? "#1a3558" : "#dde3ec", color: kategorie === k.key ? "white" : "#333" }} onClick={() => setKategorie(k.key)}>
            {k.label} <span style={{ opacity: 0.7 }}>{k.key === "" ? eintraege.length : k.key === "favorit" ? eintraege.filter((e) => e.favorit).length : eintraege.filter((e) => e.kategorie === k.key).length}</span>
          </button>
        ))}
      </div>

      {meldung && <div style={styles.meldung} onClick={() => setMeldung("")}>{meldung}</div>}

      {eintraege.length === 0 ? (
        <div style={styles.karte}>
          <p style={{ margin: 0 }}>Der Tresor ist noch leer. Über <b>„⬆ Excel-Import“</b> kannst du deine Passwort-Tabelle übernehmen – sie wird nur in diesem Browser gelesen und verschlüsselt gespeichert.</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.tabelle}>
            <thead>
              <tr>
                {SPALTEN.map((sp) => (
                  <th key={sp.key} style={styles.th} onClick={() => setSortierung((a) => ({ spalte: sp.key, richtung: a.spalte === sp.key ? -a.richtung : 1 }))}>
                    {sp.label}{sortierung.spalte === sp.key ? (sortierung.richtung === 1 ? " ▲" : " ▼") : ""}
                  </th>
                ))}
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((e) => {
                const p = problem(e);
                const st = passwortStaerke(e.passwort);
                return (
                  <tr key={e.id} style={{ backgroundColor: e.kaputt ? "#fdecea" : "white" }}>
                    <td style={styles.td}>
                      <b style={{ cursor: "pointer" }} onClick={() => setBearbeiten(e)}>{e.favorit ? "⭐ " : ""}{e.titel}</b>
                      {e.typ && <div style={styles.klein}>{e.typ}</div>}
                      {e.notizen && <div style={{ ...styles.klein, whiteSpace: "pre-line", maxHeight: "3.2em", overflow: "hidden" }} title={e.notizen}>📝 {e.notizen}</div>}
                    </td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{katInfo(e.kategorie).icon} {katInfo(e.kategorie).label}</td>
                    <td style={styles.td}>{e.organisation}</td>
                    <td style={styles.td}>
                      {e.benutzer && <span style={styles.kopierbar} title="Kopieren" onClick={() => kopieren(e.benutzer, "Benutzer")}>{e.benutzer}</span>}
                    </td>
                    <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                      {e.passwort ? <>
                        <span style={{ fontFamily: "monospace", cursor: "pointer" }} title="Kopieren" onClick={() => kopieren(e.passwort, "Passwort")}>{sichtbar[e.id] ? e.passwort : "••••••••"}</span>
                        <button style={styles.iconBtn} title={sichtbar[e.id] ? "Verbergen" : "Anzeigen"} onClick={() => setSichtbar({ ...sichtbar, [e.id]: !sichtbar[e.id] })}>{sichtbar[e.id] ? "🙈" : "👁️"}</button>
                        <button style={styles.iconBtn} title="Passwort kopieren" onClick={() => kopieren(e.passwort, "Passwort")}>📋</button>
                        <div>
                          <span style={{ ...styles.badge, backgroundColor: STAERKE[st].farbe }}>{STAERKE[st].label}</span>
                          {p.mehrfach && <span style={{ ...styles.badge, backgroundColor: "#e53935" }} title={`Wird in ${mehrfach[e.passwort]} Einträgen verwendet`}>{mehrfach[e.passwort]}× verwendet</span>}
                        </div>
                      </> : <span style={styles.klein}>–</span>}
                    </td>
                    <td style={{ ...styles.td, maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.url && <a href={/^https?:\/\//i.test(e.url) ? e.url : `https://${e.url}`} target="_blank" rel="noopener noreferrer" title={e.url}>{e.url.replace(/^https?:\/\/(www\.)?/i, "")}</a>}
                    </td>
                    <td style={styles.td}><button style={styles.iconBtn} title="Bearbeiten" onClick={() => setBearbeiten(e)}>✏️</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {gefiltert.length === 0 && <p style={styles.leer}>Keine Einträge passen zur Auswahl.</p>}
        </div>
      )}

      <p style={{ ...styles.klein, marginTop: "1rem" }}>
        🔒 Verschlüsselt im Browser (AES-256, Schlüssel aus dem Master-Passwort). Automatische Sperre nach 10 Minuten ohne Aktivität.
      </p>

      {bearbeiten && <EintragFormular eintrag={bearbeiten.id ? bearbeiten : null} onSpeichern={speichern} onAbbrechen={() => setBearbeiten(null)} onLoeschen={loeschen} />}
    </Seite>
  );
}

function Kachel({ wert, label, farbe, aktiv, onClick }) {
  return (
    <div style={{ ...styles.kachel, cursor: onClick ? "pointer" : "default", outline: aktiv ? `2px solid ${farbe}` : "none" }} onClick={onClick}>
      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: farbe }}>{wert}</div>
      <div style={{ fontSize: "0.75rem", color: "#666" }}>{label}{onClick ? (aktiv ? " · Filter aktiv" : " · anklicken zum Filtern") : ""}</div>
    </div>
  );
}

const styles = {
  karte: { backgroundColor: "white", borderRadius: "6px", padding: "1rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  karteTitel: { fontWeight: "bold", marginBottom: "0.7rem", color: "#1a3558" },
  hinweis: { fontSize: "0.85rem", color: "#555", lineHeight: 1.45, marginTop: 0 },
  input: { padding: "0.55rem", fontSize: "0.92rem", border: "1px solid #ccc", borderRadius: "4px", width: "100%", boxSizing: "border-box" },
  label: { display: "flex", flexDirection: "column", fontSize: "0.8rem", color: "#555", gap: "0.2rem" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.7rem" },
  button: { padding: "0.55rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.88rem", whiteSpace: "nowrap" },
  buttonGrau: { padding: "0.55rem 1rem", backgroundColor: "#dde3ec", color: "#333", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.88rem", whiteSpace: "nowrap" },
  buttonDunkel: { padding: "0.55rem 1rem", backgroundColor: "#1a3558", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.88rem", whiteSpace: "nowrap" },
  buttonRot: { padding: "0.55rem 1rem", backgroundColor: "#ea4335", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "0.88rem" },
  kleinBtn: { padding: "0.3rem 0.5rem", background: "#f0f2f5", border: "1px solid #ddd", borderRadius: "4px", cursor: "pointer" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "0.95rem", padding: "0 0.15rem" },
  fehler: { color: "#c0392b", background: "#fdecea", padding: "0.5rem 0.7rem", borderRadius: "4px", marginTop: "0.6rem", fontSize: "0.85rem" },
  meldung: { backgroundColor: "#e8f0fe", color: "#1a3558", padding: "0.6rem 0.8rem", borderRadius: "4px", marginBottom: "1rem", cursor: "pointer", fontSize: "0.88rem" },
  kacheln: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "0.8rem", marginBottom: "1rem" },
  kachel: { backgroundColor: "white", borderRadius: "6px", padding: "0.8rem", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  leiste: { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.8rem", alignItems: "center" },
  chip: { padding: "0.35rem 0.75rem", border: "none", borderRadius: "16px", cursor: "pointer", fontSize: "0.82rem" },
  tabelle: { width: "100%", borderCollapse: "collapse", backgroundColor: "white", borderRadius: "6px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", fontSize: "0.85rem" },
  th: { backgroundColor: "#1a3558", color: "white", textAlign: "left", padding: "0.55rem 0.7rem", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.3px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" },
  td: { padding: "0.5rem 0.7rem", borderBottom: "1px solid #eee", verticalAlign: "top" },
  klein: { fontSize: "0.74rem", color: "#888" },
  kopierbar: { cursor: "pointer", borderBottom: "1px dashed #bbb" },
  badge: { display: "inline-block", fontSize: "0.66rem", color: "white", padding: "0.05rem 0.4rem", borderRadius: "8px", marginRight: "0.25rem", marginTop: "0.2rem" },
  overlay: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.35)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3vh 1rem", overflowY: "auto", zIndex: 1000 },
  dialog: { backgroundColor: "white", borderRadius: "10px", padding: "1.3rem", width: "760px", maxWidth: "100%", boxSizing: "border-box", boxShadow: "0 8px 30px rgba(0,0,0,0.25)" },
  leer: { color: "#999", textAlign: "center" },
};
